import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};

async function _fetchPermisos() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/permisos/mios`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d.permisos || {};
  } catch (_) { return null; }
}

async function _fetchFlags() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feature-flags`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return null;
    return await r.json();
  } catch (_) { return null; }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permisos, setPermisos] = useState(null); // { recurso: { acciones, condicion } }
  const [flags, setFlags] = useState(null);        // { key: bool } — null mientras carga
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const userData = await authApi.getMe();
      setUser(userData);
      const [perms, flagsData] = await Promise.all([_fetchPermisos(), _fetchFlags()]);
      setPermisos(perms);
      setFlags(flagsData);
    } catch (_) {
      localStorage.removeItem('token');
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await authApi.login(email, password);
    localStorage.setItem('token', res.access_token);
    setUser(res.user);
    const [perms, flagsData] = await Promise.all([_fetchPermisos(), _fetchFlags()]);
    setPermisos(perms);
    setFlags(flagsData);
    return res;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setPermisos(null);
    setFlags(null);
  };

  // Helpers de permisos reutilizables en toda la app.
  // isSuperAdmin → configuración del sistema (usuarios, zonas, permisos, scheduler…).
  // isAdmin → gestión comercial avanzada (incluye admin_comercial).
  const isSuperAdmin = user?.rol === 'admin' || user?.rol === 'dir_comercial';
  const isAdmin = isSuperAdmin || user?.rol === 'admin_comercial';
  const isComercial = user?.rol === 'comercial';

  // hasPermission(recurso, accion) — admin SIEMPRE true. Resto consulta /permisos/mios.
  const hasPermission = useCallback((recurso, accion = 'leer') => {
    if (!user) return false;
    if (user.rol === 'admin') return true;
    if (!permisos) return false; // aún cargando → ocultar por defecto
    return Boolean(permisos[recurso]?.acciones?.[accion]);
  }, [user, permisos]);

  // hasFeature(key) — admin SIEMPRE true. Resto consulta feature-flags.
  // Si flags aún no cargaron (null) → true por defecto (no esconder hasta saber).
  const hasFeature = useCallback((key) => {
    if (!user) return false;
    if (user.rol === 'admin') return true;
    if (flags === null) return true;
    return flags[key] !== false;
  }, [user, flags]);

  return (
    <AuthContext.Provider value={{ user, permisos, flags, loading, login, logout, isSuperAdmin, isAdmin, isComercial, hasPermission, hasFeature }}>
      {children}
    </AuthContext.Provider>
  );
};
